import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layers, Plus, PackageX } from "lucide-react";
import api, { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function LotBuys() {
  const [lots, setLots] = useState(null);
  const { add } = useCart();

  useEffect(() => {
    api.get("/lot-buys").then((r) => setLots(r.data)).catch(() => setLots([]));
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8" data-testid="lot-buys-page">
      <div className="bg-link text-white border-2 border-ink p-6 sm:p-10 mb-8">
        <div className="inline-flex items-center gap-2 bg-white text-ink font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-1 mb-4">
          <Layers className="h-3.5 w-3.5" /> Bulk Bargains
        </div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">LOT BUYS</h1>
        <p className="font-mono text-sm text-white/85 mt-4 max-w-xl leading-relaxed">
          Our deepest markdowns, grouped. Each lot bundles items that already hit rock-bottom pricing —
          then takes an <strong>extra 20% off</strong> the combined total. Buy the whole lot in one click.
        </p>
      </div>

      {lots === null ? (
        <div className="font-mono text-subtle">Loading lot buys…</div>
      ) : lots.length === 0 ? (
        <div className="border-2 border-dashed border-subtle p-12 text-center" data-testid="lot-buys-empty">
          <PackageX className="h-8 w-8 mx-auto mb-3 text-subtle" />
          <p className="font-mono text-sm text-subtle max-w-md mx-auto">No lot buys available right now. Check back soon — we bundle stock into discounted lots as it hits the floor.</p>
          <Link to="/deals" className="inline-block mt-5 bg-ink text-paper px-6 h-10 leading-10 font-mono text-xs uppercase tracking-wider hover:bg-link transition-colors">See Markdowns</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {lots.map((lot) => {
            const save = lot.combined_total > 0 ? Math.round((1 - lot.lot_price / lot.combined_total) * 100) : 0;
            return (
              <div key={lot.id} data-testid={`lot-buy-card-${lot.id}`} className="border-2 border-ink bg-paper flex flex-col">
                <div className="relative grid grid-cols-4 border-b border-ink bg-surface">
                  {(lot.images && lot.images.length ? lot.images : [""]).slice(0, 4).map((im, i) => (
                    <div key={i} className="aspect-square border-r border-ink last:border-r-0 overflow-hidden">
                      {im ? <img src={im} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center"><PackageX className="h-5 w-5 text-subtle" /></div>}
                    </div>
                  ))}
                  <div className="absolute top-0 left-0 bg-danger text-white font-heading font-black px-2 py-1 border-r border-b border-ink">-{save}%</div>
                </div>

                <div className="p-4 flex flex-col flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-subtle">{lot.sku} · {lot.items.length} items</div>
                  <div className="font-heading font-black text-xl tracking-tighter">{lot.name}</div>

                  <ul className="mt-3 space-y-1 font-mono text-xs text-subtle max-h-32 overflow-y-auto">
                    {lot.items.map((it) => (
                      <li key={it.id} className="flex justify-between gap-2">
                        <span className="truncate">{it.name}</span>
                        <span className="whitespace-nowrap">×{it.quantity} · {money(it.our_price)}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto pt-4 flex items-end justify-between">
                    <div>
                      <div className="font-mono text-xs text-subtle line-through">{money(lot.combined_total)}</div>
                      <div className="font-heading font-black text-3xl tracking-tighter leading-none">{money(lot.lot_price)}</div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-danger mt-1">Whole lot · sold once</div>
                    </div>
                    <button
                      data-testid={`lot-buy-add-${lot.id}`}
                      onClick={() => { add(lot, 1); toast.success(`${lot.name} added to cart`); }}
                      className="flex items-center gap-2 bg-ink text-paper h-11 px-5 font-mono text-xs uppercase tracking-wider hover:bg-link transition-colors"
                    >
                      <Plus className="h-4 w-4" /> Add Lot
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
